/**
 * OpenAI Chat Integration for JMEFit Chatbot
 * 
 * This module provides functions for working with OpenAI's API
 * to power the JMEFit AI assistant with structured responses,
 * interactive buttons, and personalized fitness & nutrition guidance.
 */

import OpenAI from 'openai';
import { STRIPE_PRODUCTS, PRICING, getPriceAmount, formatPrice } from './stripe-products';
import { z } from 'zod';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY || process.env.OPENAI_API_KEY,
  dangerouslyAllowBrowser: true // Only for client-side usage
});

// Structured response schemas
const ChatResponseSchema = z.object({
  message: z.string(),
  type: z.enum(['text', 'program_list', 'recommendation', 'nutrition_guide', 'quick_actions']),
  data: z.optional(z.any()),
  quick_replies: z.optional(z.array(z.object({
    text: z.string(),
    action: z.string(),
    payload: z.optional(z.any())
  }))),
  actions: z.optional(z.array(z.object({
    type: z.enum(['add_to_cart', 'navigate', 'contact']),
    label: z.string(),
    data: z.any()
  })))
});

type ChatResponse = z.infer<typeof ChatResponseSchema>;

// Program information with current pricing
const getJMEFitPrograms = () => [
  {
    id: 'nutrition-only',
    name: 'Nutrition Only',
    price: {
      monthly: formatPrice(getPriceAmount('NUTRITION_ONLY', 'month')),
      yearly: formatPrice(getPriceAmount('NUTRITION_ONLY', 'year'))
    },
    description: 'Personalized nutrition plans with macro coaching and weekly check-ins',
    features: [
      'Custom macronutrient calculations',
      'Weekly check-ins with adjustments',
      'Meal planning guidance',
      '24/7 messaging support',
      'Macro coaching guidebook'
    ],
    commitment: '3-month minimum',
    stripePriceIds: {
      monthly: 'price_1RUDhrG00IiCtQkDvaOpyt6s',
      yearly: 'price_1RUDijG00IiCtQkDe1qmQ0eK'
    }
  },
  {
    id: 'nutrition-training',
    name: 'Nutrition & Training',
    price: {
      monthly: formatPrice(getPriceAmount('NUTRITION_TRAINING', 'month')),
      yearly: formatPrice(getPriceAmount('NUTRITION_TRAINING', 'year'))
    },
    description: 'Complete transformation package with nutrition and custom workout plans',
    features: [
      'Everything in Nutrition Only',
      'Custom workout programming',
      'Form check video reviews',
      'Exercise library access',
      'Progress tracking tools'
    ],
    commitment: '3-month minimum',
    popular: true,
    stripePriceIds: {
      monthly: 'price_1RUDi3G00IiCtQkD4uRbb21q',
      yearly: 'price_1RUDioG00IiCtQkDpJ8b1kWn'
    }
  },
  {
    id: 'self-led-training',
    name: 'Self-Led Training',
    price: {
      monthly: formatPrice(getPriceAmount('SELF_LED_TRAINING', 'month')),
      yearly: formatPrice(getPriceAmount('SELF_LED_TRAINING', 'year'))
    },
    description: 'Complete app access with monthly workout plans and exercise library',
    features: [
      'Access to all workout programs',
      'Exercise demo videos',
      'Progress tracking',
      'Workout scheduling',
      'No long-term commitment'
    ],
    commitment: 'Month-to-month',
    stripePriceIds: {
      monthly: 'price_1RUDiCG00IiCtQkDoCAAviT3',
      yearly: 'price_1RUDitG00IiCtQkDnlnwaDnP'
    }
  },
  {
    id: 'trainer-feedback',
    name: 'Trainer Feedback',
    price: {
      monthly: formatPrice(getPriceAmount('TRAINER_FEEDBACK', 'month')),
      yearly: formatPrice(getPriceAmount('TRAINER_FEEDBACK', 'year'))
    },
    description: 'Premium training with personal form checks and direct trainer communication',
    features: [
      'Everything in Self-Led Training',
      'Personal form check videos',
      'Direct messaging with trainers',
      'Workout adaptations',
      'Premium support'
    ],
    commitment: 'Month-to-month',
    stripePriceIds: {
      monthly: 'price_1RUDiLG00IiCtQkDXmMxfyQV',
      yearly: 'price_1RUDixG00IiCtQkDrQevwF4a'
    }
  },
  {
    id: 'shred-challenge',
    name: 'SHRED Challenge',
    price: {
      oneTime: formatPrice(getPriceAmount('SHRED_CHALLENGE'))
    },
    description: '6-week intensive transformation challenge for rapid results',
    features: [
      'Intensive 6-week program',
      'Custom fat loss macros',
      'Daily workouts (5 per week)',
      'Interactive check-ins',
      'Private community access',
      'Home and gym options'
    ],
    commitment: '6 weeks',
    billingInterval: 'one-time',
    stripePriceIds: {
      oneTime: 'price_1RUDieG00IiCtQkDPXpSZB8x'
    }
  },
  {
    id: 'one-time-macros',
    name: 'One-Time Macros Calculation',
    price: {
      oneTime: formatPrice(getPriceAmount('ONE_TIME_MACROS'))
    },
    description: 'Complete macro calculation with comprehensive guides and meal templates',
    features: [
      'Custom macronutrient calculation',
      'Detailed macro coaching guidebook',
      'Meal planning templates',
      'Grocery list suggestions',
      'Supplement recommendations'
    ],
    commitment: 'One-time purchase',
    billingInterval: 'one-time',
    stripePriceIds: {
      oneTime: 'price_1RUDiUG00IiCtQkD2xc2mQSs'
    }
  }
];

// Cached responses for instant replies to common actions
const CACHED_RESPONSES = {
  compare_features: {
    message: "Let's compare the features of JMEFit's programs to find the best fit for you! 🌟",
    type: 'program_list' as const,
    data: {
      programs: getJMEFitPrograms()
    },
    quick_replies: [
      { text: '✨ Get My Recommendation', action: 'get_recommendation' },
      { text: '💬 Ask Questions', action: 'ask_questions' },
      { text: '🥗 Nutrition Focus', action: 'nutrition_guide' }
    ]
  },
  
  show_programs: {
    message: "Here are all our amazing JMEFit programs! Each one is designed to help you reach your specific goals 💪",
    type: 'program_list' as const,
    data: {
      programs: getJMEFitPrograms()
    },
    quick_replies: [
      { text: '🎯 Which is best for me?', action: 'get_recommendation' },
      { text: '📊 Compare Features', action: 'compare_features' },
      { text: '💬 I have questions', action: 'ask_questions' }
    ]
  },
  
  nutrition_guide: {
    message: "Great question! Nutrition is absolutely crucial for your results. Here's what I always tell our clients:",
    type: 'nutrition_guide' as const,
    data: {
      tips: [
        '🥗 Focus on whole foods - they fuel your body better than processed stuff',
        '💧 Stay hydrated! Aim for at least 8-10 glasses of water daily',
        '🍗 Include protein with every meal to maintain and build muscle',
        '⏰ Time your carbs around workouts for better energy and recovery',
        '🍽️ Don\'t skip meals - consistency keeps your metabolism happy'
      ],
      programs: [
        'Nutrition Only Program - I\'ll personally calculate your macros and check in weekly',
        'Nutrition & Training - Complete transformation with both nutrition and workouts',
        'One-Time Macros - Perfect if you want to do it yourself with proper guidance'
      ]
    },
    quick_replies: [
      { text: '🍽️ Nutrition Only Program', action: 'nutrition_only' },
      { text: '💪 Full Transformation', action: 'nutrition_training' },
      { text: '📊 Just Macro Calculation', action: 'macro_calculation' }
    ]
  },
  
  get_recommendation: {
    message: "I'd love to help you find the perfect program! Let me ask you a few quick questions to give you the best recommendation:",
    type: 'quick_actions' as const,
    quick_replies: [
      { text: '🍽️ I mainly want nutrition help', action: 'recommend_nutrition' },
      { text: '💪 I want nutrition + training', action: 'recommend_full' },
      { text: '🏃‍♀️ Just workouts, I handle food', action: 'recommend_training' },
      { text: '⚡ I want fast results', action: 'recommend_shred' }
    ]
  }
};

// System prompt for more human-like conversation
const SYSTEM_PROMPT = `You are Jamie's personal AI assistant for JMEFit - think of yourself as Jamie's right-hand person who knows everything about the programs and genuinely cares about helping people transform their lives.

PERSONALITY:
- Friendly, encouraging, and knowledgeable like a supportive fitness friend
- Use a conversational, warm tone - not robotic or sales-y
- Show genuine enthusiasm for helping people reach their goals
- Ask follow-up questions to better understand their needs
- Use emojis naturally to add warmth (but don't overdo it)
- Share insights like you've helped hundreds of people succeed

ABOUT JMEFIT:
JMEFit is Jamie's premium fitness and nutrition coaching service. We focus on sustainable, science-based approaches that actually work long-term. Jamie personally designed these programs based on years of helping people transform their bodies and relationships with food.

CURRENT PROGRAMS & PRICING:
${getJMEFitPrograms().map(program => `
${program.name}: ${program.price.monthly ? `$${program.price.monthly}/month` : `$${program.price.oneTime} one-time`}
- ${program.description}
- Perfect for: ${program.commitment}
- Key benefits: ${program.features.slice(0, 2).join(', ')}
`).join('\n')}

HOW TO RESPOND:
1. Always sound human and conversational, like you're texting a friend
2. Ask clarifying questions to understand their goals better
3. Give specific, actionable advice based on their situation
4. Share why certain programs work well for different goals
5. Be honest about what each program involves (time, commitment, etc.)
6. Use personal touches like "I've seen great results with..." or "Many of our clients love..."

RESPONSE FORMAT:
Always respond with JSON in this exact structure:
{
  "message": "Your warm, human-like response here",
  "type": "text|program_list|recommendation|nutrition_guide|quick_actions",
  "data": { optional: "additional data" },
  "quick_replies": [
    { "text": "Button text", "action": "action_type" }
  ]
}

Remember: You're not just selling programs - you're helping people change their lives. Be genuinely helpful and supportive!`;

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatSession {
  id: string;
  messages: ChatMessage[];
  context: {
    userGoals?: string[];
    experienceLevel?: string;
    interestedPrograms?: string[];
    budget?: string;
  };
}

export const createChatSession = (): ChatSession => {
  return {
    id: `chat_${Date.now()}`,
    messages: [
      {
        role: 'system',
        content: SYSTEM_PROMPT
      }
    ],
    context: {}
  };
};

export const sendMessage = async (
  session: ChatSession,
  message: string
): Promise<ChatResponse> => {
  try {
    // Add user message to session
    session.messages.push({
      role: 'user',
      content: message
    });

    // Analyze user message for context
    const userGoals = extractGoals(message);
    const experienceLevel = extractExperienceLevel(message);
    const interestedPrograms = extractProgramInterest(message);

    // Update session context
    if (userGoals.length > 0) {
      session.context.userGoals = [...(session.context.userGoals || []), ...userGoals];
    }
    if (experienceLevel) {
      session.context.experienceLevel = experienceLevel;
    }
    if (interestedPrograms.length > 0) {
      session.context.interestedPrograms = [...(session.context.interestedPrograms || []), ...interestedPrograms];
    }

    // Check for cached responses first (for instant replies)
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('compare') && lowerMessage.includes('feature')) {
      return CACHED_RESPONSES.compare_features;
    }
    
    if ((lowerMessage.includes('show') && lowerMessage.includes('program')) || 
        lowerMessage.includes('view programs') || lowerMessage.includes('all programs')) {
      return CACHED_RESPONSES.show_programs;
    }
    
    if (lowerMessage.includes('nutrition') && (lowerMessage.includes('help') || lowerMessage.includes('guide') || lowerMessage.includes('advice'))) {
      return CACHED_RESPONSES.nutrition_guide;
    }
    
    if (lowerMessage.includes('recommend') || lowerMessage.includes('best program') || lowerMessage.includes('which program')) {
      return CACHED_RESPONSES.get_recommendation;
    }

    // Handle specific recommendation requests
    if (lowerMessage.includes('nutrition help') || lowerMessage.includes('mainly want nutrition')) {
      const nutritionProgram = getJMEFitPrograms()[0]; // Nutrition Only
      return {
        message: "Perfect! If nutrition is your main focus, I'd definitely recommend our Nutrition Only Program. It's exactly what you need - Jamie will personally calculate your macros and check in with you weekly to make adjustments. Most people see amazing results focusing just on nutrition first! 🎯",
        type: 'recommendation',
        data: {
          ...nutritionProgram,
          reasoning: 'This program is perfect for people who want to focus on getting their nutrition dialed in first. Many clients find that getting their food right is actually 80% of their results!'
        },
        quick_replies: [
          { text: '🛒 Add to Cart', action: 'add_to_cart', payload: nutritionProgram },
          { text: '🤔 Tell me more', action: 'nutrition_details' },
          { text: '📊 Compare all programs', action: 'compare_features' }
        ]
      };
    }

    if (lowerMessage.includes('nutrition + training') || lowerMessage.includes('full transformation') || lowerMessage.includes('complete package')) {
      const fullProgram = getJMEFitPrograms()[1]; // Nutrition & Training
      return {
        message: "Excellent choice! The Nutrition & Training program is our most popular for a reason - you get everything you need for a complete transformation. Jamie handles both your nutrition AND creates custom workouts just for you. Plus you get form check videos so you know you're doing everything right! 🔥",
        type: 'recommendation',
        data: {
          ...fullProgram,
          reasoning: 'This is our most comprehensive program and gets the fastest results because you\'re optimizing both nutrition and training together.'
        },
        quick_replies: [
          { text: '🛒 Add to Cart', action: 'add_to_cart', payload: fullProgram },
          { text: '💪 What workouts are included?', action: 'workout_details' },
          { text: '📊 Compare all programs', action: 'compare_features' }
        ]
      };
    }

    // If OpenAI API key is not available, provide enhanced fallback responses
    if (!import.meta.env.VITE_OPENAI_API_KEY && !process.env.OPENAI_API_KEY) {
      console.warn('OpenAI API key not configured, using enhanced fallback responses');
      
      // More human-like fallback responses
      if (lowerMessage.includes('goal') || lowerMessage.includes('want to') || lowerMessage.includes('trying to')) {
        return {
          message: "I'd love to help you reach your goals! What are you most focused on right now - losing weight, building muscle, or just getting healthier overall? Once I know more about what you're working toward, I can point you to the perfect program! 😊",
          type: 'text',
          quick_replies: [
            { text: '🔥 Lose weight', action: 'goal_weight_loss' },
            { text: '💪 Build muscle', action: 'goal_muscle_gain' },
            { text: '🍽️ Fix my nutrition', action: 'nutrition_guide' },
            { text: '📊 Show me all options', action: 'show_programs' }
          ]
        };
      }
      
      if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('how much')) {
        return {
          message: `Great question! Our programs range from $${PRICING.SELF_LED.monthly}/month for self-led training up to $${PRICING.NUTRITION_TRAINING.monthly}/month for full nutrition and training coaching. The investment depends on how much support you want. What level of guidance are you looking for? 💰`,
          type: 'text',
          quick_replies: [
            { text: '🍽️ Mainly nutrition help', action: 'recommend_nutrition' },
            { text: '💪 Complete transformation', action: 'recommend_full' },
            { text: '🏃‍♀️ Just workouts', action: 'recommend_training' },
            { text: '📊 See all pricing', action: 'show_programs' }
          ]
        };
      }
      
      if (lowerMessage.includes('how') || lowerMessage.includes('work') || lowerMessage.includes('process')) {
        return {
          message: "I'm so glad you asked! Here's how it works: After you sign up, Jamie personally reviews your goals and creates your custom plan. You'll get weekly check-ins, can message anytime with questions, and everything adjusts as you progress. It's like having a personal trainer and nutritionist in your pocket! 📱",
          type: 'text',
          quick_replies: [
            { text: '🎯 Get my recommendation', action: 'get_recommendation' },
            { text: '📞 I want to talk to someone', action: 'contact' },
            { text: '📊 Compare programs', action: 'compare_features' }
          ]
        };
      }
      
      return {
        message: "I'm here to help you find the perfect fitness program! I know all about Jamie's programs and love helping people figure out what'll work best for their goals and lifestyle. What would you like to know? 😊",
        type: 'text',
        quick_replies: [
          { text: '📊 Show me all programs', action: 'show_programs' },
          { text: '🎯 What\'s best for me?', action: 'get_recommendation' },
          { text: '🥗 Nutrition questions', action: 'nutrition_guide' },
          { text: '💬 I have other questions', action: 'ask_questions' }
        ]
      };
    }

    // Create enhanced prompt with context for OpenAI
    const contextPrompt = createContextualPrompt(session.context, message);

    // Call OpenAI with improved prompting
    const completion = await openai.chat.completions.create({
      model: "gpt-4-1106-preview",
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: contextPrompt }
      ],
      response_format: {
        type: "json_object"
      },
      max_tokens: 1000,
      temperature: 0.7
    });

    const responseContent = completion.choices[0]?.message?.content;
    if (!responseContent) {
      throw new Error('No response from OpenAI');
    }

    // Parse and validate the response
    const parsedResponse = JSON.parse(responseContent);
    const validatedResponse = ChatResponseSchema.parse(parsedResponse);

    // Add assistant message to session
    session.messages.push({
      role: 'assistant',
      content: validatedResponse.message
    });

    return validatedResponse;

  } catch (error) {
    console.error('Error in OpenAI chat:', error);
    
    // Enhanced fallback response based on message content
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('help') || lowerMessage.includes('question')) {
      return {
        message: "I'm here to help! I know it can be overwhelming trying to figure out the right fitness program. Let me make it simple - what's your main goal right now? I can point you in exactly the right direction! 😊",
        type: 'text',
        quick_replies: [
          { text: '🔥 Lose weight', action: 'goal_weight_loss' },
          { text: '💪 Build muscle', action: 'goal_muscle_gain' },
          { text: '🍽️ Fix my nutrition', action: 'nutrition_guide' },
          { text: '📊 Just show me everything', action: 'show_programs' }
        ]
      };
    }
    
    // Default friendly fallback
    return {
      message: "Hey! I'm Jamie's AI assistant and I'm here to help you find the perfect program for your goals. Every program is designed based on real results from hundreds of successful transformations. What can I help you with today? 😊",
      type: 'text',
      quick_replies: [
        { text: '📊 View All Programs', action: 'show_programs' },
        { text: '🎯 Get Recommendation', action: 'get_recommendation' },
        { text: '🥗 Nutrition Help', action: 'nutrition_guide' },
        { text: '💬 Contact Support', action: 'contact' }
      ]
    };
  }
};

// Helper functions for context extraction
const extractGoals = (message: string): string[] => {
  const goals: string[] = [];
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('lose weight') || lowerMessage.includes('fat loss') || lowerMessage.includes('cut')) {
    goals.push('weight_loss');
  }
  if (lowerMessage.includes('build muscle') || lowerMessage.includes('gain muscle') || lowerMessage.includes('bulk')) {
    goals.push('muscle_gain');
  }
  if (lowerMessage.includes('get fit') || lowerMessage.includes('fitness') || lowerMessage.includes('health')) {
    goals.push('general_fitness');
  }
  if (lowerMessage.includes('strength') || lowerMessage.includes('stronger')) {
    goals.push('strength');
  }
  if (lowerMessage.includes('nutrition') || lowerMessage.includes('diet') || lowerMessage.includes('eating')) {
    goals.push('nutrition');
  }
  
  return goals;
};

const extractExperienceLevel = (message: string): string | undefined => {
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('beginner') || lowerMessage.includes('new to') || lowerMessage.includes('just started')) {
    return 'beginner';
  }
  if (lowerMessage.includes('intermediate') || lowerMessage.includes('some experience')) {
    return 'intermediate';
  }
  if (lowerMessage.includes('advanced') || lowerMessage.includes('experienced') || lowerMessage.includes('years')) {
    return 'advanced';
  }
  
  return undefined;
};

const extractProgramInterest = (message: string): string[] => {
  const programs: string[] = [];
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('nutrition only')) {
    programs.push('nutrition-only');
  }
  if (lowerMessage.includes('nutrition') && lowerMessage.includes('training')) {
    programs.push('nutrition-training');
  }
  if (lowerMessage.includes('self-led') || lowerMessage.includes('self led')) {
    programs.push('self-led-training');
  }
  if (lowerMessage.includes('trainer feedback') || lowerMessage.includes('feedback')) {
    programs.push('trainer-feedback');
  }
  if (lowerMessage.includes('shred') || lowerMessage.includes('challenge')) {
    programs.push('shred-challenge');
  }
  
  return programs;
};

const createContextualPrompt = (context: ChatSession['context'], message: string): string => {
  let prompt = `User message: "${message}"\n\n`;
  
  if (context.userGoals && context.userGoals.length > 0) {
    prompt += `User goals: ${context.userGoals.join(', ')}\n`;
  }
  if (context.experienceLevel) {
    prompt += `Experience level: ${context.experienceLevel}\n`;
  }
  if (context.interestedPrograms && context.interestedPrograms.length > 0) {
    prompt += `Previously interested in: ${context.interestedPrograms.join(', ')}\n`;
  }
  
  prompt += `\nPlease respond with a JSON object following this exact structure:
{
  "message": "Your helpful response text",
  "type": "text|program_list|recommendation|nutrition_guide|quick_actions",
  "data": { optional: "additional data for the response type" },
  "quick_replies": [
    { "text": "Button text", "action": "action_type", "payload": "optional data" }
  ],
  "actions": [
    { "type": "add_to_cart|navigate|contact", "label": "Button label", "data": {} }
  ]
}

Guidelines:
- Keep messages conversational and encouraging
- Include relevant quick replies for common next steps
- Use type "program_list" when showing multiple programs
- Use type "recommendation" when suggesting a specific program
- Include action buttons when users show purchase intent
- Always include current pricing and accurate program details
- For navigate actions, use RELATIVE URLs only (e.g., "/programs", "/nutrition-only", NOT "https://jmefit.com/programs")
- Never include full domain URLs for internal pages to prevent duplication`;

  return prompt;
};

export const getProgramRecommendation = async (userPreferences: any): Promise<any> => {
  const programs = getJMEFitPrograms();
  
  // Simple recommendation logic based on preferences
  let recommendedProgram = programs[1]; // Default to Nutrition & Training
  
  if (userPreferences.goals?.includes('nutrition') && !userPreferences.goals?.includes('training')) {
    recommendedProgram = programs[0]; // Nutrition Only
  } else if (userPreferences.budget === 'low' || userPreferences.timeAvailable === 'limited') {
    recommendedProgram = programs[2]; // Self-Led Training
  } else if (userPreferences.goals?.includes('quick') || userPreferences.goals?.includes('challenge')) {
    recommendedProgram = programs[4]; // SHRED Challenge
  }
  
  return {
    program: recommendedProgram.name,
    reasoning: `Based on your preferences, this program offers the best combination of features and value for your goals.`,
    features: recommendedProgram.features,
    price: recommendedProgram.price.monthly || recommendedProgram.price.oneTime,
    id: recommendedProgram.id
  };
};

export { getJMEFitPrograms, type ChatResponse, type ChatSession }; 